// app/frontend/components/layouts/AuthFormLayout.tsx
import { ReactNode, useState } from "react";
import {
  Box,
  Container,
  Group,
  Burger,
  Stack,
  Divider,
  Button,
} from "@mantine/core";
import { useMediaQuery } from "@mantine/hooks";
import { Link } from "@inertiajs/react";
import {
  IconVideo,
  IconBook2,
  IconNews,
  IconInfoCircle,
  IconCurrencyDollar,
  IconHelpCircle,
} from "@tabler/icons-react";
import { L2KH3, L2KH4 } from "../ui/Typography";
import { NavigationDrawer } from "../ui/NavigationDrawer";
import { MenuNavItem } from "../ui/MenuNavItem";

interface AuthFormLayoutProps {
  children: ReactNode;
  maxWidth?: number;
}

/**
 * AuthFormLayout - Centered layout for authentication forms (login, register, etc.)
 * Features a gradient background with a narrow centered card
 * Mobile-optimized with tighter spacing and form positioned higher
 */
export default function AuthFormLayout({
  children,
  maxWidth = 420,
}: AuthFormLayoutProps) {
  const isMobile = useMediaQuery("(max-width: 768px)");
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  return (
    <Box
      style={{
        minHeight: "100vh",
        background: `
          radial-gradient(ellipse at 20% 50%, rgba(255, 152, 0, 0.15) 0%, transparent 50%),
          radial-gradient(ellipse at 80% 20%, rgba(255, 193, 7, 0.12) 0%, transparent 50%),
          radial-gradient(ellipse at 40% 80%, rgba(255, 87, 34, 0.1) 0%, transparent 50%),
          linear-gradient(135deg, #171717 0%, #1a1a1a 25%, #171717 50%, #1f1f1f 75%, #171717 100%)
        `,
        display: "flex",
        flexDirection: "column",
      }}
    >
      {/* Header - more compact on mobile */}
      <Box
        py={isMobile ? "xs" : "md"}
        px={isMobile ? "md" : "xl"}
        style={{
          borderBottom: "1px solid rgba(255, 255, 255, 0.1)",
          background: "rgba(0, 0, 0, 0.3)",
        }}
      >
        <Group gap="sm" wrap="nowrap">
          <Burger
            opened={mobileMenuOpen}
            onClick={() => setMobileMenuOpen((v) => !v)}
            size="sm"
            color="#FF9800"
            aria-label="Open navigation menu"
          />
          <Link href="/" style={{ textDecoration: "none" }}>
            {isMobile ? (
              <L2KH4 mb={0}>
                <span style={{ color: "#FF9800" }}>Learn</span>
                <span style={{ color: "#F0F0F0" }}>tokiz</span>
              </L2KH4>
            ) : (
              <L2KH3 mb={0}>
                <span style={{ color: "#FF9800" }}>Learn</span>
                <span style={{ color: "#F0F0F0" }}>tokiz</span>
              </L2KH3>
            )}
          </Link>
        </Group>
      </Box>

      <NavigationDrawer
        opened={mobileMenuOpen}
        onClose={() => setMobileMenuOpen(false)}
        title="Menu"
        size="xs"
      >
        <Stack gap="lg">
          <Stack gap={8} pt="md">
            <MenuNavItem
              href="/videos"
              icon={IconVideo}
              label="Free Videos"
              onClick={() => setMobileMenuOpen(false)}
            />
            <MenuNavItem
              href="/dictionary"
              icon={IconBook2}
              label="Dictionary"
              onClick={() => setMobileMenuOpen(false)}
            />
            <MenuNavItem
              href="/posts"
              icon={IconNews}
              label="Posts"
              onClick={() => setMobileMenuOpen(false)}
            />
            <MenuNavItem
              href="#"
              icon={IconInfoCircle}
              label="About"
              disabled
              onClick={() => setMobileMenuOpen(false)}
            />
            <MenuNavItem
              href="#"
              icon={IconCurrencyDollar}
              label="Pricing"
              disabled
              onClick={() => setMobileMenuOpen(false)}
            />
            <MenuNavItem
              href="/faq"
              icon={IconHelpCircle}
              label="FAQ"
              onClick={() => setMobileMenuOpen(false)}
            />
          </Stack>

          <Divider color="rgba(255,255,255,0.1)" />

          <Stack gap="md">
            <Button
              component={Link}
              href="/login"
              variant="light"
              c="#FF9800"
              fullWidth
              styles={{ root: { fontWeight: 600 } }}
              onClick={() => setMobileMenuOpen(false)}
            >
              Login
            </Button>
            <Button
              disabled
              variant="filled"
              fullWidth
              title="Sign up opens at 2.0 launch"
              styles={{
                root: {
                  background: "rgba(255, 152, 0, 0.15)",
                  border: "1px solid rgba(255, 152, 0, 0.25)",
                  color: "rgba(255, 152, 0, 0.6)",
                  fontWeight: 600,
                  cursor: "not-allowed",
                },
              }}
            >
              Sign Up · Coming soon
            </Button>
          </Stack>
        </Stack>
      </NavigationDrawer>

      {/* Content - positioned higher on page, not vertically centered */}
      <Box
        style={{
          flex: 1,
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          padding: isMobile ? "24px 16px" : "48px 24px",
          paddingTop: isMobile ? "32px" : "64px",
        }}
      >
        <Container size={maxWidth} style={{ width: "100%" }}>
          {children}
        </Container>
      </Box>

      {/* Footer - more compact on mobile */}
      <Box
        py={isMobile ? "xs" : "md"}
        px={isMobile ? "md" : "xl"}
        ta="center"
        style={{
          borderTop: "1px solid rgba(255, 255, 255, 0.1)",
          background: "rgba(0, 0, 0, 0.3)",
          color: "#888",
          fontFamily: "'Space Grotesk', sans-serif",
          fontSize: isMobile ? "11px" : "12px",
        }}
      >
        © {new Date().getFullYear()} Learntokiz. All rights reserved.
      </Box>
    </Box>
  );
}
